import React, { useState, useImperativeHandle, forwardRef } from 'react';
import { Form, Input } from 'antd';
import { useTranslation } from 'react-i18next';
import { EyeInvisibleOutlined, EyeTwoTone } from '@ant-design/icons';
import './PasswordCreationForm.scss';

// Prevent copy, cut, paste on password input
const handlePasswordCopy = (e: React.ClipboardEvent<HTMLInputElement>) => {
  e.preventDefault();
};

const handlePasswordCut = (e: React.ClipboardEvent<HTMLInputElement>) => {
  e.preventDefault();
};

const handlePasswordPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
  e.preventDefault();
};

export interface PasswordValidation {
  hasNumber: boolean;
  hasUppercase: boolean;
  hasLowercase: boolean;
  hasSpecialChar: boolean;
  validLength: boolean;
}

export interface PasswordCreationFormProps {
  showValidationIndicators?: boolean;
  disabled?: boolean; // Add disabled attribute
}

export interface PasswordCreationFormRef {
  isPasswordValid: () => boolean;
  resetValidation: () => void;
}

const PasswordCreationForm = forwardRef<
  PasswordCreationFormRef,
  PasswordCreationFormProps
>(({ showValidationIndicators = true, disabled = false }, ref) => {
  const { t } = useTranslation('password_form');
  const form = Form.useFormInstance();
  const [validation, setValidation] = useState<PasswordValidation>({
    hasNumber: false,
    hasUppercase: false,
    hasLowercase: false,
    hasSpecialChar: false,
    validLength: false,
  });

  // Password validation function
  const validatePassword = (password: string): PasswordValidation => {
    return {
      hasNumber: /\d/.test(password),
      hasUppercase: /[A-Z]/.test(password),
      hasLowercase: /[a-z]/.test(password),
      hasSpecialChar: /[!@#$%?&*(^)]/.test(password),
      validLength: password.length >= 8 && password.length <= 20,
    };
  };

  // Get current password value from form for validation indicators
  const password = Form.useWatch('newPassword', form);

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    const newValidation = validatePassword(newPassword);
    setValidation(newValidation);
  };

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    isPasswordValid: () => {
      const password = form.getFieldValue('newPassword');
      const confirmPassword = form.getFieldValue('confirmPassword');
      return (
        validation.hasNumber &&
        validation.hasUppercase &&
        validation.hasLowercase &&
        validation.hasSpecialChar &&
        validation.validLength &&
        password === confirmPassword
      );
    },
    resetValidation: () => {
      form.setFieldsValue({
        ['newPassword']: '',
        ['confirmPassword']: '',
      });
      setValidation({
        hasNumber: false,
        hasUppercase: false,
        hasLowercase: false,
        hasSpecialChar: false,
        validLength: false,
      });
    },
  }));

  const ValidationIndicator: React.FC<{ isValid: boolean; text: string }> = ({
    isValid,
    text,
  }) => (
    <div className={`validation-item ${isValid ? 'valid' : 'invalid'}`}>
      <span className="validation-icon">{isValid ? '✓' : '✗'}</span>
      <span className="validation-text">{text}</span>
    </div>
  );

  return (
    <div className="password-creation-form-container">
      {/* Password */}
      <Form.Item
        name="newPassword"
        label={t('passwordLabel')}
        rules={[
          {
            required: true,
            message: t('validation.passwordRequired'),
          },
          {
            validator: (_, value) => {
              if (!value) {
                return Promise.resolve();
              }
              const passwordValidation = validatePassword(value);
              if (
                passwordValidation.hasNumber &&
                passwordValidation.hasUppercase &&
                passwordValidation.hasLowercase &&
                passwordValidation.hasSpecialChar &&
                passwordValidation.validLength
              ) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(''));
            },
          },
        ]}
      >
        <Input.Password
          placeholder={t('passwordPlaceholder')}
          iconRender={(visible) =>
            visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
          }
          className="password-input"
          onChange={handlePasswordChange}
          onCopy={handlePasswordCopy}
          onCut={handlePasswordCut}
          onPaste={handlePasswordPaste}
          disabled={disabled} // Disable on submit
        />
      </Form.Item>

      {/* Password Validation Indicators */}
      {showValidationIndicators && password && (
        <div className="password-validation">
          <ValidationIndicator
            isValid={validation.validLength}
            text={t('validation.validLength')}
          />
          <ValidationIndicator
            isValid={validation.hasNumber}
            text={t('validation.hasNumber')}
          />
          <ValidationIndicator
            isValid={validation.hasUppercase}
            text={t('validation.hasUppercase')}
          />
          <ValidationIndicator
            isValid={validation.hasSpecialChar}
            text={t('validation.hasSpecialChar')}
          />
          <ValidationIndicator
            isValid={validation.hasLowercase}
            text={t('validation.hasLowercase')}
          />
        </div>
      )}

      {/* Confirm Password */}
      <Form.Item
        name="confirmPassword"
        label={t('confirmPasswordLabel')}
        rules={[
          {
            required: true,
            message: t('validation.confirmPasswordRequired'),
          },
          {
            validator: (_, value) => {
              if (!value || password === value) {
                return Promise.resolve();
              }
              return Promise.reject(new Error(t('validation.passwordMismatch')));
            },
          },
        ]}
      >
        <Input.Password
          placeholder={t('confirmPasswordPlaceholder')}
          iconRender={(visible) =>
            visible ? <EyeTwoTone /> : <EyeInvisibleOutlined />
          }
          className="password-input"
          onCopy={handlePasswordCopy}
          onCut={handlePasswordCut}
          onPaste={handlePasswordPaste}
          disabled={disabled} // Disable on submit
        />
      </Form.Item>
    </div>
  );
});

PasswordCreationForm.displayName = 'PasswordCreationForm';

export default PasswordCreationForm;